import React, { useEffect, useState, useCallback } from 'react';
import { FaLessThan, FaGreaterThan, FaArrowRight } from "react-icons/fa";
import Aos from 'aos';
import 'aos/dist/aos.css';
import benefitsData from '../assets/data/benefitsData';
import retentionBannerImg from "../assets/retention_banner.jpg"; 
import benefitsPromoImg from "../assets/benefits_promo.jpg"; 

const Hero = () => {
  // Store the starting item pointer instead of a chunk index
  const [startIndex, setStartIndex] = useState(0);
  const [autoSlideInterval, setAutoSlideInterval] = useState(null);

  const totalItems = benefitsData.length;

  // Shifts window forward by 1 item continuously
  const nextSlide = useCallback(() => {
    setStartIndex((prevIndex) => (prevIndex + 1) % totalItems);
  }, [totalItems]);

  // Shifts window backward by 1 item continuously
  const previousSlide = useCallback(() => {
    setStartIndex((prevIndex) => (prevIndex - 1 + totalItems) % totalItems);
  }, [totalItems]);

  useEffect(() => {
    Aos.init({ duration: 1200, once: true });

    const interval = setInterval(() => {
      nextSlide();
    }, 4000); 

    setAutoSlideInterval(interval);
    return () => clearInterval(interval);
  }, [nextSlide]); // Added nextSlide as dependency

  const handleManualNavigation = () => {
    if (autoSlideInterval) {
      clearInterval(autoSlideInterval);
    }
    const interval = setInterval(() => {
      nextSlide();
    }, 4000);
    setAutoSlideInterval(interval);
  };

  // Generate exactly 4 items dynamically, looping seamlessly back to index 0 if it overshoots array boundaries
  const getVisibleBenefits = () => {
    const items = [];
    for (let i = 0; i < 4; i++) {
      items.push(benefitsData[(startIndex + i) % totalItems]);
    }
    return items;
  };

  const visibleBenefits = getVisibleBenefits();

  return (
    <div className='w-full min-h-screen bg-white px-6 py-12 md:px-16 lg:px-24 xl:px-32 flex flex-col gap-16 md:gap-24 font-sans'>
      
      {/* ================= TOP CONTAINER: DATA RETENTION SERVICES ================= */}
      <section 
        className='w-full bg-[#E3F2FD] rounded-[32px] p-6 md:p-10 lg:p-14 flex flex-col gap-8 shadow-sm max-w-[1200px] mx-auto'
        data-aos="fade-up"
      >
        <div className='w-full rounded-2xl overflow-hidden h-[180px] sm:h-[240px] md:h-[280px] lg:h-[320px]'>
          <img 
            src={retentionBannerImg} 
            alt="Data Retention" 
            className='w-full h-full object-cover object-center select-none pointer-events-none'
          />
        </div>

        <div className='flex flex-col gap-4 max-w-[1050px]'>
          <h2 className='text-[#060080] text-3xl md:text-[40px] font-bold tracking-tight leading-tight'>
            Data Retention Services
          </h2>
          <p className='text-[#4A5568] text-base md:text-lg leading-relaxed font-normal'>
            Andurar Cloud's Data Retention Service ensures that critical business data is stored securely and remains accessible to authorized persons when needed. We provide flexible and compliant data storage solutions, ensuring your data is protected, retrievable, and meets regulatory standards for long-term storage.
          </p>
        </div>
      </section>

      {/* ================= BOTTOM CONTAINER: BENEFITS CONSOLE ================= */}
      <section className='w-full max-w-[1200px] mx-auto flex flex-col gap-8' data-aos="fade-up">
        
        {/* Row Header & Carousel Controller */}
        <div className='flex items-center justify-between pl-2 pr-2'>
          <h2 className='text-black text-4xl md:text-[52px] font-bold tracking-tight'>
            Benefits
          </h2>
          
          <div className='flex gap-3'>
            <button 
              className='bg-[#F1F5F9] hover:bg-[#E2E8F0] h-11 w-11 rounded-full flex justify-center items-center text-[#4A5568] transition' 
              onClick={() => { handleManualNavigation(); previousSlide(); }}
            >
              <FaLessThan className='text-sm' />
            </button>
            <button 
              className='bg-[#F1F5F9] hover:bg-[#E2E8F0] h-11 w-11 rounded-full flex justify-center items-center text-[#4A5568] transition' 
              onClick={() => { handleManualNavigation(); nextSlide(); }}
            >
              <FaGreaterThan className='text-sm' />
            </button>
          </div>
        </div>

        {/* Master Layout Frame */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch'>
          
          {/* Tall Marketing Promo Container (Twice the vertical height of a single card slot) */}
          <div className='bg-[#FBBF24] rounded-[28px] p-8 flex flex-col justify-between gap-8 relative overflow-hidden shadow-sm h-full min-h-[500px] lg:min-h-[580px] lg:col-span-1'>
            <div className='flex flex-col gap-6 items-start z-10'>
              <h3 className='text-[#060080] text-3xl md:text-[34px] font-bold leading-tight tracking-tight'>
                Benefits of<br />Andurar Cloud<br />Services
              </h3>
              <button className='bg-black text-white px-5 py-3 rounded-full flex items-center justify-center gap-2 text-sm font-semibold shadow hover:bg-opacity-80 transition'>
                Explore More
                <FaArrowRight className='text-xs' />
              </button>
            </div>

            <div className='w-full mt-auto flex justify-center items-end pointer-events-none z-0'>
              <img 
                src={benefitsPromoImg} 
                alt="Andurar Promo Team" 
                className='max-h-[280px] lg:max-h-[340px] w-auto object-contain object-bottom'
              />
            </div>
          </div>

          {/* 
            Modular Content Sub-Grid:
            Displays 4 fully populated data containers simultaneously on desktop screen configurations!
          */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8 lg:col-span-2 content-start'>
            {visibleBenefits.map((benefit, index) => (
              <div 
                key={`${benefit.title}-${index}`} // Composite key ensures animation registers correctly during slide changes
                className='bg-white border-2 border-[#E2E8F0] rounded-[24px] p-6 lg:p-8 flex flex-col gap-4 justify-start shadow-sm hover:border-[#BFDBFE] transition-all duration-300 min-h-[240px] lg:min-h-[276px]'
              >
                {benefit.img && (
                  <img 
                    src={benefit.img} 
                    alt="Metric Icon" 
                    className='h-10 w-10 object-contain self-start'
                  />
                )}
                <h4 className='text-[#060080] text-xl lg:text-2xl font-bold tracking-tight'>
                  {benefit.title}
                </h4>
                <p className='text-[#4A5568] text-sm lg:text-base leading-relaxed font-normal'>
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

    </div>
  );
};

export default Hero;